import React, { useState, useEffect, useContext } from "react";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Tab,
  Tabs,
} from "@mui/material";
import { UserContext } from "../context/UserContext";
import API_BASE_URL from "../config";

const fetchData = async (userId, category) => {
  try {
    let response;

    switch (category) {
      case "Jobs":
        response = await fetch(`${API_BASE_URL}/applynowjob/user/${userId}`);
        break;

      case "Services":
        response = await fetch(`${API_BASE_URL}/services/notifications/${userId}`);
        break;

      case "Classifieds":
        response = await fetch(`${API_BASE_URL}/classifieds/notifications/${userId}`);
        break;

      case "Events":
        response = await fetch(`${API_BASE_URL}/event-bookings/${userId}`);
        break;

      default:
        return { data: [], category: "" };
    }

    const data = await response.json();

    // Ensure data is always an array
    const normalizedData = Array.isArray(data) ? data : data ? [data] : [];

    return { data: normalizedData, category };
  } catch (error) {
    console.error("Error fetching data:", error);
    return { data: [], category };
  }
};


const MyBookings = () => {
  const { user } = useContext(UserContext);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("Jobs");
  const [data, setData] = useState([]);

  useEffect(() => {
    if (user && user.id) {
      setLoading(true);
      fetchData(user.id, selectedCategory)
        .then(({ data }) => {
          setData(data);
        })
        .catch((error) => {
          console.error("Error fetching user data:", error);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [user, selectedCategory]);

  const handleCategoryChange = (event, newValue) => {
    setSelectedCategory(newValue);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 5, mb:4 }}>
      <Typography
        variant="h4"
        color="#42326e"
        fontWeight="bold"
        textAlign="center"
        mb={3}
      >
        My Bookings
      </Typography>

      <Tabs value={selectedCategory} onChange={handleCategoryChange} centered>
        <Tab label="Jobs" value="Jobs" />
        <Tab label="Services" value="Services" />
        <Tab label="Classifieds" value="Classifieds" />
        <Tab label="Events" value="Events" />
      </Tabs>

      {loading ? (
        <CircularProgress sx={{ display: "block", margin: "auto", mt: 5 }} />
      ) : (
        <>
          {/* Jobs */}
          {selectedCategory === "Jobs" && (
            <>
              <Typography
                variant="h5"
                color="secondary"
                fontWeight="bold"
                textAlign="center"
                mt={5}
                mb={2}
              >
                Applied Jobs
              </Typography>
              {data.length === 0 ? (
                <Typography textAlign="center" color="textSecondary">
                  No job applications found.
                </Typography>
              ) : (
                <Grid container spacing={3}>
                  {data.map((job) => (
                    <Grid item xs={12} sm={6} md={4} key={job.id}>
                      <Card sx={{ boxShadow: 3, p: 2 }}>
                        <CardContent>
                          <Typography variant="h6" fontWeight="bold">
                            Job Title: {job.job_title}
                          </Typography>
                          <Typography>Company Name: {job.company_name}</Typography>
                          <Typography>Company Website: {job.company_website}</Typography>
                          <Typography>Experience: {job.experience}</Typography>
                          <Typography>
                            Applied on: {new Date(job.applied_at).toLocaleDateString()}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </>
          )}

          {/* Services */}
          {selectedCategory === "Services" && (
            <>
              <Typography
                variant="h5"
                color="secondary"
                fontWeight="bold"
                textAlign="center"
                mt={5}
                mb={2}
              >
                Services
              </Typography>
              {data.filter((d) => d.opportunity_type === "service").length === 0 ? (
                <Typography textAlign="center" color="textSecondary">
                  No Services available.
                </Typography>
              ) : (
                <Grid container spacing={3}>
                  {data
                    .filter((d) => d.opportunity_type === "service")
                    .map((item) => (
                      <Grid item xs={12} sm={6} md={4} key={item.id}>
                        <Card sx={{ boxShadow: 3, p: 2 }}>
                          <CardContent>
                            <Typography variant="h6" fontWeight="bold">
                              {item.message2}
                            </Typography>
                            <Typography variant="h6" fontWeight="bold">
                              {item.response}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                </Grid>
              )}
            </>
          )}

          {/* Classifieds */}
          {selectedCategory === "Classifieds" && (
            <>
              <Typography
                variant="h5"
                color="secondary"
                fontWeight="bold"
                textAlign="center"
                mt={5}
                mb={2}
              >
                Classifieds
              </Typography>
              {data.filter((d) => d.opportunity_type === "classified").length === 0 ? (
                <Typography textAlign="center" color="textSecondary">
                  No Classifieds available.
                </Typography>
              ) : (
                <Grid container spacing={3}>
                  {data
                    .filter((d) => d.opportunity_type === "classified")
                    .map((item) => (
                      <Grid item xs={12} sm={6} md={4} key={item.id}>
                        <Card sx={{ boxShadow: 3, p: 2 }}>
                          <CardContent>
                            <Typography variant="h6" fontWeight="bold">
                              {item.message2}
                            </Typography>
                            <Typography variant="h6" fontWeight="bold">
                              {item.response}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                </Grid>
              )}
            </>
          )}

          {/* Events */}
          {selectedCategory === "Events" && (
            <>
              <Typography
                variant="h5"
                color="secondary"
                fontWeight="bold"
                textAlign="center"
                mt={5}
                mb={2}
              >
                Booked Events
              </Typography>
              {data.filter((event) => event.event_name && event.event_name.trim() !== "").length === 0 ? (
                <Typography textAlign="center" color="textSecondary">
                  No events booked.
                </Typography>
              ) : (
                <Grid container spacing={3}>
                  {data.filter((event) => event.event_name && event.event_name.trim() !== "").map((event) => (
                    <Grid item xs={12} sm={6} md={4} key={event.id}>
                      <Card sx={{ boxShadow: 3, p: 2 }}>
                        <CardContent>
                          <Typography variant="h6" fontWeight="bold">
                            Event Title: {event.event_name}
                          </Typography>
                          <Typography>Description: {event.description}</Typography>
                          <Typography>
                            Date: {new Date(event.event_date).toLocaleDateString()}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </>
          )}
        </>
      )}
    </Container>
  );
};

export default MyBookings;
